import { useEffect, useState } from 'react';

const YOUTUBE_INIT_FALLBACK_WAIT_TIME_MS = 2000;

let isLoaded = false;
let isLoading = false;
let callbacks: (() => void)[] = [];
let fallbackTimeout: NodeJS.Timeout;
let oldHandleYouTubeIframeAPIReady: undefined | (() => void);

const handleYouTubeIframeAPIReady = () => {
    oldHandleYouTubeIframeAPIReady?.();
    isLoaded = true;
    callbacks.forEach((callback) => callback());
    callbacks = [];
};

export function useYouTubeApiLoader(): boolean {
    const [ready, setReady] = useState(() => isLoaded || (typeof window !== 'undefined' && !!window.YT));

    useEffect(() => {
        // We check for `window.YT` because `window.onYouTubeIframeAPIReady` is only called once. If this hook is
        // invoked after it's called, `ready` would remain false otherwise
        if (isLoaded || window.YT) return;

        callbacks.push(() => setReady(true));

        if (isLoading) return;
        isLoading = true;

        if (window.onYouTubeIframeAPIReady !== handleYouTubeIframeAPIReady) {
            // Ensuing we don't override other mechanisms that depend on this, e.g. Quantum Metric:
            oldHandleYouTubeIframeAPIReady = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = handleYouTubeIframeAPIReady;
        }

        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            document.body.appendChild(script);
        }

        if (!fallbackTimeout) {
            // Provided in case a script that we don't control (e.g. a tag manager import) overrides
            // `window.onYouTubeIframeAPIReady`, which stops this hook's `callbacks` from being ran
            fallbackTimeout = setTimeout(() => {
                if (!isLoaded && window.YT) {
                    handleYouTubeIframeAPIReady();
                } else if (!window.YT) {
                    console.error('useYouTubeApiLoader: YouTube script failed to load!');
                }
            }, YOUTUBE_INIT_FALLBACK_WAIT_TIME_MS);
        }
    }, []);

    return ready;
}
